import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { taskRequestApi } from '../services/taskRequestApi';
import { searchCustomers, getCustomerLocations } from '@features/coverage/services/customerApi';
import { useAuth } from '@shared/context/useAuth';
import type { TaskType, TaskPriority } from '../types/taskRequest';
import type { Customer, CustomerLocation } from '@features/coverage/types/clientClinic';
import { withXmId } from '@shared/utils/xmId';

interface CreateTaskDialogProps {
  onCreated: (taskId: number) => void;
  onCancel: () => void;
}

export default function CreateTaskDialog({ onCreated, onCancel }: Readonly<CreateTaskDialogProps>) {
  const { user } = useAuth();

  // Core task fields
  const [taskType, setTaskType] = useState<TaskType | ''>('');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<TaskPriority>(2);
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [requestedBy, setRequestedBy] = useState(user?.displayName ?? '');
  const [clinicId, setClinicId] = useState('');

  // Customer search
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  // Location
  const [locations, setLocations] = useState<CustomerLocation[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');

  // Address (auto-populated from location, editable)
  const [street, setStreet] = useState('');
  const [suite, setSuite] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced customer search
  useEffect(() => {
    if (customerSearchTimer.current) {
      clearTimeout(customerSearchTimer.current);
    }
    if (customerSearch.length < 2) {
      setCustomers([]);
      setShowCustomerDropdown(false);
      return;
    }
    setIsSearchingCustomers(true);
    customerSearchTimer.current = setTimeout(async () => {
      try {
        const results = await searchCustomers(customerSearch, 15);
        setCustomers(results);
        setShowCustomerDropdown(true);
      } catch {
        setCustomers([]);
      } finally {
        setIsSearchingCustomers(false);
      }
    }, 300);
    return () => {
      if (customerSearchTimer.current) clearTimeout(customerSearchTimer.current);
    };
  }, [customerSearch]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load locations when customer changes
  useEffect(() => {
    if (!selectedCustomer) {
      setLocations([]);
      setSelectedLocationId('');
      return;
    }
    setIsLoadingLocations(true);
    getCustomerLocations(selectedCustomer.id)
      .then((locs) => setLocations(locs))
      .catch(() => setLocations([]))
      .finally(() => setIsLoadingLocations(false));
  }, [selectedCustomer]);

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
    // Reset location & address
    setSelectedLocationId('');
    setStreet('');
    setSuite('');
    setCity('');
    setState('');
    setZip('');
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomers([]);
    setLocations([]);
    setSelectedLocationId('');
    setStreet('');
    setSuite('');
    setCity('');
    setState('');
    setZip('');
  };

  const handleLocationChange = (locationId: string) => {
    setSelectedLocationId(locationId);
    if (locationId) {
      const loc = locations.find((l) => l.id === Number(locationId));
      if (loc) {
        setStreet(loc.street ?? '');
        setSuite(loc.suite ?? '');
        setCity(loc.city ?? '');
        setState(loc.state ?? '');
        setZip(loc.zip ?? '');
      }
    } else {
      setStreet('');
      setSuite('');
      setCity('');
      setState('');
      setZip('');
    }
  };

  const hasAddress = street || city || state || zip;

  const isValid =
    taskType !== '' && title.trim().length > 0 && requestedBy.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    // Build requestPayload JSON with address + context
    let requestPayload: string | undefined;
    if (hasAddress || selectedCustomer || selectedLocationId) {
      const payload: Record<string, unknown> = {};
      if (selectedCustomer) {
        payload.customerName = selectedCustomer.name;
      }
      if (selectedLocationId) {
        const loc = locations.find((l) => l.id === Number(selectedLocationId));
        if (loc) payload.locationName = loc.name;
      }
      if (hasAddress) {
        payload.address = {
          street: street.trim() || undefined,
          suite: suite.trim() || undefined,
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          zip: zip.trim() || undefined,
        };
      }
      requestPayload = JSON.stringify(payload);
    }

    try {
      const result = await taskRequestApi.createTaskRequest({
        taskType: taskType as TaskType,
        title: title.trim(),
        priority,
        description: description.trim() || undefined,
        source: 'Manual',
        requestedBy: requestedBy.trim(),
        dueDate: dueDate || undefined,
        customerId: selectedCustomer?.id,
        customerLocationId: selectedLocationId ? Number(selectedLocationId) : undefined,
        clinicId: clinicId ? Number(clinicId) : undefined,
        requestPayload,
      });
      onCreated(result.id);
    } catch {
      setError('Failed to create task. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="task-dialog-overlay" onClick={onCancel}>
      <div className="task-dialog task-dialog-wide" onClick={(e) => e.stopPropagation()}>
        <div className="task-dialog-header">
          <h3>Create Task</h3>
        </div>
        <div className="task-dialog-body">
          {error && (
            <p className="task-dialog-error">
              {error}
            </p>
          )}

          <div className="task-dialog-field">
            <label htmlFor="create-task-type">Task Type (required)</label>
            <select
              id="create-task-type"
              value={taskType}
              onChange={(e) => setTaskType(e.target.value as TaskType | '')}
              autoFocus
            >
              <option value="" disabled>Select a task type...</option>
              <option value="SetupNewClinic">Setup New Clinic</option>
              {/* ImAddClinic is system-created by the simple-add flow (always tied to a clinic),
                  so it's intentionally not manually creatable here — only filterable. */}
              <option value="CreateClinicRelationship">Create Clinic Relationship</option>
            </select>
          </div>

          <div className="task-dialog-field">
            <label htmlFor="create-task-title">Title (required)</label>
            <input
              id="create-task-title"
              type="text"
              placeholder="Brief summary of the task..."
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 200))}
            />
            <div className="task-dialog-char-count">{title.length}/200</div>
          </div>

          {/* Customer & Location Section */}
          <div className="task-dialog-field" ref={customerDropdownRef}>
            <label htmlFor="create-task-customer">Customer</label>
            <div className="task-customer-search-wrapper">
              <input
                id="create-task-customer"
                type="text"
                placeholder="Search by name or account number..."
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  if (selectedCustomer) {
                    setSelectedCustomer(null);
                    setLocations([]);
                    setSelectedLocationId('');
                  }
                }}
              />
              {(selectedCustomer || customerSearch) && (
                <button
                  type="button"
                  className="task-filter-search-clear"
                  onClick={handleClearCustomer}
                  aria-label="Clear customer"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {showCustomerDropdown && !selectedCustomer && (
              <div className="task-user-search-results">
                {isSearchingCustomers ? (
                  <div className="task-user-search-empty">Searching...</div>
                ) : customers.length > 0 ? (
                  customers.map((c) => (
                    <div
                      key={c.id}
                      className="task-user-search-item"
                      onClick={() => handleSelectCustomer(c)}
                    >
                      <span className="task-user-search-name">{withXmId(c.name, c.xmId)}</span>
                      {c.accountNumber && (
                        <span className="task-user-search-email">{c.accountNumber}</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="task-user-search-empty">No customers found</div>
                )}
              </div>
            )}
          </div>

          <div className="task-dialog-grid-2">
            <div className="task-dialog-field">
              <label htmlFor="create-task-location">Location</label>
              <select
                id="create-task-location"
                value={selectedLocationId}
                onChange={(e) => handleLocationChange(e.target.value)}
                disabled={!selectedCustomer || isLoadingLocations}
              >
                <option value="">
                  {!selectedCustomer
                    ? 'Select a customer first'
                    : isLoadingLocations
                      ? 'Loading locations...'
                      : locations.length === 0
                        ? 'No locations'
                        : 'Select a location...'}
                </option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}{loc.city && loc.state ? ` — ${loc.city}, ${loc.state}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="task-dialog-field">
              <label htmlFor="create-task-clinic-id">Clinic ID</label>
              <input
                id="create-task-clinic-id"
                type="number"
                placeholder="Optional"
                value={clinicId}
                onChange={(e) => setClinicId(e.target.value)}
                min={0}
              />
            </div>
          </div>

          <div className="task-dialog-field">
            <label htmlFor="create-task-description">Description</label>
            <textarea
              id="create-task-description"
              placeholder="Additional details..."
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 2000))}
              rows={3}
            />
            <div className="task-dialog-char-count">{description.length}/2000</div>
          </div>

          <div className="task-dialog-field">
            <label htmlFor="create-task-requested-by">Requested By (required)</label>
            <input
              id="create-task-requested-by"
              type="text"
              placeholder="Name or system identifier"
              value={requestedBy}
              onChange={(e) => setRequestedBy(e.target.value)}
            />
          </div>

          <div className="task-dialog-grid-2">
            <div className="task-dialog-field">
              <label htmlFor="create-task-priority">Priority</label>
              <select
                id="create-task-priority"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value) as TaskPriority)}
              >
                <option value={1}>Low</option>
                <option value={2}>Normal</option>
                <option value={3}>High</option>
                <option value={4}>Urgent</option>
              </select>
            </div>

            <div className="task-dialog-field">
              <label htmlFor="create-task-due-date">Due Date</label>
              <input
                id="create-task-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* Address fields — shown when location selected or user wants to enter manually */}
          {(selectedLocationId || hasAddress) && (
            <>
              <div className="task-dialog-section-label task-dialog-section-label-sub">
                Address (from location — editable)
              </div>
              <div className="task-dialog-field">
                <label htmlFor="create-task-street">Street</label>
                <input
                  id="create-task-street"
                  type="text"
                  placeholder="Street address"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                />
              </div>
              <div className="task-dialog-field">
                <label htmlFor="create-task-suite">Suite / Unit</label>
                <input
                  id="create-task-suite"
                  type="text"
                  placeholder="Optional"
                  value={suite}
                  onChange={(e) => setSuite(e.target.value)}
                />
              </div>
              <div className="task-dialog-grid-city-state-zip">
                <div className="task-dialog-field">
                  <label htmlFor="create-task-city">City</label>
                  <input
                    id="create-task-city"
                    type="text"
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div className="task-dialog-field">
                  <label htmlFor="create-task-state">State</label>
                  <input
                    id="create-task-state"
                    type="text"
                    placeholder="ST"
                    maxLength={4}
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  />
                </div>
                <div className="task-dialog-field">
                  <label htmlFor="create-task-zip">Zip</label>
                  <input
                    id="create-task-zip"
                    type="text"
                    placeholder="Zip"
                    maxLength={10}
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="task-dialog-footer">
          <button
            type="button"
            className="task-action-btn task-action-btn-secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="task-action-btn task-action-btn-primary"
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
